import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchHomepageData, fetchLiveStats, incrementSearchCount } from '../../api/homepageApi';

// Fetch all homepage data
export const fetchHomepageDataAsync = createAsyncThunk(
  'homepage/fetchHomepageData',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchHomepageData();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch homepage data');
    }
  }
);

// Fetch live stats
export const fetchLiveStatsAsync = createAsyncThunk(
  'homepage/fetchLiveStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchLiveStats();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch live stats');
    }
  }
);

// Increment search count
export const incrementSearchCountAsync = createAsyncThunk(
  'homepage/incrementSearchCount',
  async ({ text, type }, { rejectWithValue }) => {
    try {
      await incrementSearchCount(text, type);
      return { text, type };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to increment search count');
    }
  }
);

const initialState = {
  // Homepage data
  stats: {},
  testimonials: [],
  features: [],
  configuration: [],
  searchSuggestions: [],
  locations: [],
  searchOptions: {
    specialties: [],
    languages: [],
  },
  
  // Loading states
  loading: false,
  statsLoading: false,
  
  // Error states
  error: null,
  statsError: null,
  
  // Last fetch timestamps
  lastFetch: null,
  lastStatsFetch: null,
};

const homepageSlice = createSlice({
  name: 'homepage',
  initialState,
  reducers: {
    clearHomepageData: (state) => {
      state.stats = {};
      state.testimonials = [];
      state.features = [];
      state.configuration = [];
      state.searchSuggestions = [];
      state.locations = [];
      state.searchOptions = { specialties: [], languages: [] };
      state.lastFetch = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.statsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch homepage data
      .addCase(fetchHomepageDataAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomepageDataAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats || {};
        state.testimonials = action.payload.testimonials || [];
        state.features = action.payload.features || [];
        state.configuration = action.payload.configuration || [];
        state.searchSuggestions = action.payload.search_suggestions || [];
        state.locations = action.payload.locations || [];
        state.searchOptions = action.payload.search_options || { specialties: [], languages: [] };
        state.lastFetch = Date.now();
      })
      .addCase(fetchHomepageDataAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch live stats
      .addCase(fetchLiveStatsAsync.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchLiveStatsAsync.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
        state.lastStatsFetch = Date.now();
      })
      .addCase(fetchLiveStatsAsync.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })
      
      // Increment search count
      .addCase(incrementSearchCountAsync.fulfilled, (state, action) => {
        // Update search suggestion count if needed
        const { text, type } = action.payload;
        const suggestion = state.searchSuggestions.find(
          s => s.text === text && s.suggestion_type === type
        );
        if (suggestion) {
          suggestion.search_count += 1;
        }
      });
  },
});

export const { clearHomepageData, clearErrors } = homepageSlice.actions;

// Selectors
export const selectHomepageData = (state) => state.homepage;
export const selectStats = (state) => state.homepage.stats;
export const selectTestimonials = (state) => state.homepage.testimonials;
export const selectFeatures = (state) => state.homepage.features;
export const selectConfiguration = (state) => state.homepage.configuration;
export const selectSearchSuggestions = (state) => state.homepage.searchSuggestions;
export const selectLocations = (state) => state.homepage.locations;
export const selectSearchOptions = (state) => state.homepage.searchOptions;
export const selectHomepageLoading = (state) => state.homepage.loading;
export const selectStatsLoading = (state) => state.homepage.statsLoading;
export const selectHomepageError = (state) => state.homepage.error;
export const selectStatsError = (state) => state.homepage.statsError;

export default homepageSlice.reducer; 